import {
	GetSensorHistoryParams,
	NewSensorDataPointsParams,
} from '@Types/editSensorData.types';
import axios, { AxiosResponse } from 'axios';

import { SensorParams } from '@Types/sensors.types';
import {
	FilterVirtualSensor,
	SensorsResponse,
	TimeSeriesUploadElement,
} from '@Types/sensor.types';
import configurations from '@Apis/configurations.api';
import assets from '@Apis/assets.api';
import sensors from '@Apis/sensors.api';
import { Resolutions } from '@innovyze/stylovyze';

export const getSensorsSensors = () => {
	return assets.get('/sensors');
};

export const getSensor = (sensorParams: SensorParams) => {
	const { id, ...params } = sensorParams;
	const url = encodeURI(`/sensors/${id}`);
	return assets.get(url, { params });
};

export const getVirtualSensors = (lookerParams: FilterVirtualSensor) => {
	return sensors.get('/v2/sensors', { params: lookerParams });
};

export const getVirtualSensorsCount = () => {
	return assets.get('/configurations', {
		params: {
			configType: 'sensor',
			virtualOrPhysical: 'virtual',
			offset: 1,
			limit: 1,
		},
	});
};

export const getPhysicalSensorsCount = () => {
	return assets.get('/configurations', {
		params: {
			configType: 'sensor',
			virtualOrPhysical: 'physical',
			offset: 1,
			limit: 1,
		},
	});
};

export const getSensors = (
	type: 'virtual' | 'physical' | undefined,
	sensorType: string,
) => {
	let filter: string = type ? '?channelType=' + type : '';
	if (sensorType) {
		filter += (filter ? '&' : '?') + 'sensorType=' + sensorType;
	}
	return sensors.get<SensorsResponse>('/v2/sensors' + filter);
};

export const getSensorsByType = (
	lookerParams: FilterVirtualSensor,
	isPhysical = false,
	configType = 'sensor',
) => {
	return assets.get<SensorsResponse>('/v2/sensors', {
		params: { ...lookerParams, isPhysical: isPhysical, configType },
	});
};

export const getSensorsById = (
	sensorIds: string,
	isPhysical = false,
	configType = 'sensor',
) => {
	return assets.get<SensorsResponse>('/v2/sensors', {
		params: { sensorId: sensorIds, isPhysical, configType },
	});
};

export const getSensorUploadURL = (fileName: string) =>
	configurations.post('/upload', {
		info: 'sensor',
		fileName: fileName,
		type: 'config',
	});

export const getSensorTimeSeriesUploadURL = (
	fileNames: string[],
	timeZoneIANA: string,
	sessionId: string,
	fileTimeZone: string,
): Promise<AxiosResponse<TimeSeriesUploadElement[]>> => {
	return configurations.post('/upload/time-series', {
		timezone: timeZoneIANA,
		files: fileNames,
		sessionId: sessionId,
		fileTimeZone: fileTimeZone,
	});
};

export const getAssetUploadURL = (fileName: string) =>
	configurations.post('/upload', {
		info: 'sensor',
		fileName: fileName,
		type: 'asset',
	});

export const uploadSensorConfigToS3 = (url: string, file: File) =>
	axios.put(url, file);

export const getSensorHistory = (params: GetSensorHistoryParams) => {
	const { sensorId, ...queryParams } = params;
	return configurations.get(`/v2/sensors/${sensorId}/history`, {
		params: queryParams,
	});
};

export const editSensorDataPointsService = (
	params: NewSensorDataPointsParams[],
) => {
	return configurations.post('/v2/sensors/history/bulk', [
		{
			operation: 'bulk_update',
			data: { body: params },
		},
	]);
};

export const getResolutionsBySensorId = async (sensorId: string) => {
	const response = await assets.get('/v2/sensors', {
		params: {
			configType: 'sensor',
			sourceType: 'ALL',
			limit: 1,
			offset: 1,
			sensorId,
		},
	});
	const [sensor] = response.data.sensors;
	return sensor.resolutions as Resolutions[];
};
